/**
 * Contract Error Fallback Component
 * Displays user-friendly error UI when contract editor encounters errors
 */

import { Trans } from '@lingui/react/macro';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router';

interface ContractErrorFallbackProps {
  /** Error caught by the boundary; reported to Sentry by the boundary, never shown. */
  error: Error;
  /** Function to reset error boundary state */
  resetError: () => void;
}

export function ContractErrorFallback({ resetError }: ContractErrorFallbackProps) {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/contracts');
  };

  return (
    <div className="flex items-center justify-center min-h-[600px] p-4">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
            <CardTitle>
              <Trans id="contract.error.title">Contract Editor Error</Trans>
            </CardTitle>
          </div>
          <CardDescription>
            <Trans id="contract.error.description">
              We encountered an error while loading the contract editor. This might be due to a network issue or invalid contract data.
            </Trans>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={resetError}
              variant="default"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              <Trans id="common.retry">Try again</Trans>
            </Button>
            <Button
              onClick={handleGoBack}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <Trans id="contract.error.goBack">Back to Contracts</Trans>
            </Button>
          </div>

          {/* Support message */}
          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              <Trans id="contract.error.support">
                If this problem persists, refresh the page or contact support.
              </Trans>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ContractErrorFallback;